import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddVehicleTypeModal from './AddVehicleTypeModal';
import { hasPermission } from '../../../utils/permissions';
import {
  fetchVehicleTypes,
  fetchVehicleTypeById,
  addVehicleType,
  updateVehicleType,
  selectVehicleTypes,
  selectVehicleTypesLoading,
  selectVehicleTypesError,
  selectVehicleTypesSuccessMessage,
  selectVehicleTypesTotal,
  selectVehicleTypesPage,
  selectVehicleTypesPageSize,
  selectVehicleTypesSortColumn,
  selectVehicleTypesSortDirection,
  selectVehicleTypesFilterName,
  selectSelectedVehicleType,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  deleteVehicleType,
  bulkDeleteVehicleTypes,
  clearError,
  clearSuccessMessage,
} from './slice';

function VehicleTypes({ onSelectedIdsChange, onAddClick, onBulkDeleteClick, hideButtons = false, isActive = true }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const vehicleTypes = useSelector(selectVehicleTypes);
  const loading = useSelector(selectVehicleTypesLoading);
  const error = useSelector(selectVehicleTypesError);
  const successMessage = useSelector(selectVehicleTypesSuccessMessage);
  const total = useSelector(selectVehicleTypesTotal);
  const page = useSelector(selectVehicleTypesPage);
  const pageSize = useSelector(selectVehicleTypesPageSize);
  const sortColumn = useSelector(selectVehicleTypesSortColumn);
  const sortDirection = useSelector(selectVehicleTypesSortDirection);
  const filterName = useSelector(selectVehicleTypesFilterName);
  const selectedVehicleType = useSelector(selectSelectedVehicleType);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [vehicleTypeToDelete, setVehicleTypeToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Check permissions
  const canAddVehicleType = hasPermission('vehicletype', 'addVehicleType');
  const canEditVehicleType = hasPermission('vehicletype', 'editVehicleType');
  const canDeleteVehicleType = hasPermission('vehicletype', 'deleteVehicleType');

  // Handler for selection change that also notifies parent
  const handleSelectionChange = useCallback((ids) => {
    setSelectedIds(ids);
    // Only notify parent if callback is provided and we're in embedded mode
    if (onSelectedIdsChange && hideButtons) {
      onSelectedIdsChange(ids);
    }
  }, [onSelectedIdsChange, hideButtons]);

  // Use local selectedIds
  const effectiveSelectedIds = selectedIds;

  // Fetch data whenever parent tab changes or when filters change
  useEffect(() => {
    dispatch(fetchVehicleTypes({ page, pageSize, sortColumn, sortDirection, name: filterName }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName, isActive]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(successMessage, 'success');
      dispatch(clearSuccessMessage());
      dispatch(fetchVehicleTypes({ page, pageSize, sortColumn, sortDirection, name: filterName }));
    }
  }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName]);

  // Define table columns
  const columns = [
    {
      key: 'code',
      label: t('vehicleTypes.table.code'),
    },
    {
      key: 'name',
      label: t('vehicleTypes.table.name'),
    },
    {
      key: 'description',
      label: t('vehicleTypes.table.description'),
    },
  ];

  // Handle edit action
  const handleEdit = (row) => {
    dispatch(fetchVehicleTypeById(row.id));
    setShowEditDialog(true);
  };

  // Handle delete action
  const handleDelete = (row) => {
    setVehicleTypeToDelete(row);
    setShowDeleteDialog(true);
  };

  // Confirm delete action
  const handleConfirmDelete = () => {
    if (vehicleTypeToDelete?.id) {
      dispatch(deleteVehicleType(vehicleTypeToDelete.id));
    }
    setShowDeleteDialog(false);
    setVehicleTypeToDelete(null);
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setVehicleTypeToDelete(null);
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (onBulkDeleteClick) {
      onBulkDeleteClick();
    } else {
      setShowBulkDeleteDialog(true);
    }
  };

  // Confirm bulk delete action
  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteVehicleTypes(effectiveSelectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  // Cancel bulk delete action
  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    dispatch(setPageSize(newPageSize));
  };

  // Handle sort change
  const handleSortChange = (column, direction) => {
    dispatch(setSortColumn(column));
    dispatch(setSortDirection(direction));
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    dispatch(setFilterName(event.target.value));
  };

  // Handle add vehicle type
  const handleAddVehicleType = () => {
    if (onAddClick) {
      onAddClick();
    } else {
      setShowAddDialog(true);
    }
  };

  // Handle close add vehicle type dialog
  const handleCloseAddVehicleType = () => {
    setShowAddDialog(false);
  };

  // Confirm add vehicle type
  const handleConfirmAddVehicleType = (vehicleTypeData) => {
    dispatch(addVehicleType(vehicleTypeData));
    setShowAddDialog(false);
  };

  // Handle close edit vehicle type dialog
  const handleCloseEditVehicleType = () => {
    setShowEditDialog(false);
  };

  // Confirm edit vehicle type
  const handleConfirmEditVehicleType = (vehicleTypeData) => {
    dispatch(updateVehicleType(vehicleTypeData));
    setShowEditDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, overflow: 'hidden' }}>
      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', minHeight: 0 }}>
        {/* Action Buttons - Only show if not hidden */}
        {!hideButtons && (
          <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {canAddVehicleType && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddVehicleType}
                id="addVehicleType"
                name="addVehicleType"
                data-testid="vehicleTypes-button-addVehicleType"
              >
                {t('vehicleTypes.addVehicleType')}
              </Button>
            )}
            {canDeleteVehicleType && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                disabled={effectiveSelectedIds.length === 0}
                id="deleteSelected"
                name="deleteSelected"
                data-testid="vehicleTypes-button-deleteSelected"
              >
                {t('vehicleTypes.deleteSelected')}
              </Button>
            )}
          </Box>
        )}
        {/* Filter Section */}
        <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Input
                label={t('vehicleTypes.filterByName')}
                value={filterName}
                onChange={handleFilterChange}
                placeholder={t('vehicleTypes.filterByNamePlaceholder')}
                id="filterName"
                name="filterName"
                data-testid="vehicleTypes-input-filterName"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff', minHeight: 0 }}>
          <Table
            id="vehicleTypes-table"
            columns={columns}
            data={vehicleTypes}
            selectable={true}
            selectedIds={effectiveSelectedIds}
            onSelectionChange={handleSelectionChange}
            onEdit={canEditVehicleType ? handleEdit : null}
            onDelete={canDeleteVehicleType ? handleDelete : null}
            loading={loading}
            emptyMessage={t('vehicleTypes.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            sortableColumns={['code', 'name', 'description']}
          />
        </Box>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('vehicleTypes.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('vehicleTypes.deleteDialog.message', {
              name: vehicleTypeToDelete?.name || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            name="cancelDelete"
            data-testid="vehicleTypes-button-cancelDelete"
          >
            {t('vehicleTypes.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
            data-testid="vehicleTypes-button-confirmDelete"
          >
            {t('vehicleTypes.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('vehicleTypes.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('vehicleTypes.bulkDeleteDialog.message', {
              count: effectiveSelectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
            data-testid="vehicleTypes-button-cancelBulkDelete"
          >
            {t('vehicleTypes.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
            data-testid="vehicleTypes-button-confirmBulkDelete"
          >
            {t('vehicleTypes.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Vehicle Type Modal */}
      <AddVehicleTypeModal
        open={showAddDialog}
        onClose={handleCloseAddVehicleType}
        onConfirm={handleConfirmAddVehicleType}
      />

      {/* Edit Vehicle Type Modal */}
      <AddVehicleTypeModal
        open={showEditDialog}
        onClose={handleCloseEditVehicleType}
        onConfirm={handleConfirmEditVehicleType}
        vehicleTypeData={selectedVehicleType}
        editMode={true}
      />
    </Box>
  );
}

export default VehicleTypes;
